import React, { useState } from "react";
import { Steps, Button, Form, Select, Input, message, Card } from "antd";
import { useGetTutorQuery } from "../../../../hooks/api/Students/Tutors/useGetTutorQuery";
import Loader from "../../../../components/Loader";
import { useCreatePaymentIntentMutation } from "../../../../hooks/api/Students/Bookings/useCreatePaymentIntentMutation";

const { Step } = Steps;
const { TextArea } = Input;

const TutorBookingStepper = ({
  id,
  setVisible,
}: {
  id: string;
  setVisible: any;
}) => {
  const [current, setCurrent] = useState(0);
  const [form] = Form.useForm();

  const next = async () => {
    try {
      if (current === 0) {
        if (formData.subjectId && formData.tutorShipType) {
          setCurrent(current + 1);
        } else {
          message.error("Please fill the required fields");
        }
      } else if (current === 1) {
        if (formData.startTime && formData.endTime && formData.date) {
          setCurrent(current + 1);
        } else {
          message.error("Please select a time slot");
        }
      } else {
        await form.validateFields();
        setCurrent(current + 1);
      }
    } catch (error) {}
  };

  const prev = () => {
    setCurrent(current - 1);
  };

  const onFinish = (values: any) => {
    const finalData = {
      ...formData,
      notes: values.note,
      tutorShipType: values.type,
      subjectId: values.subject,
    };
    console.log("Booking Data:", finalData);
    message.success("Booking confirmed!");
  };

  const { data: tutor, isLoading } = useGetTutorQuery({ id });

  const [formData, setFormData] = useState({
    tutorId: id,
    subjectId: "",
    tutorShipType: "",
    date: "",
    startTime: "",
    endTime: "",
    notes: "",
  });

  const initialState = {
    tutorId: "",
    subjectId: "",
    tutorShipType: "",
    date: "",
    startTime: "",
    endTime: "",
    notes: "",
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLSelectElement | HTMLInputElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectTimeSlot = (
    day: string,
    startTime: string,
    endTime: string
  ) => {
    const today = new Date();
    const dayIndex = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ].indexOf(day);
    const currentDayIndex = today.getDay();

    let offset = dayIndex - currentDayIndex;
    if (offset <= 0) {
      offset += 7;
    }

    const selectedDate = new Date(today);
    selectedDate.setDate(today.getDate() + offset);

    setFormData({
      ...formData,
      date: selectedDate.toISOString(),
      startTime,
      endTime,
    });
  };

  const handleCancelTimeSlot = () => {
    setFormData((prev) => ({
      ...prev,
      date: "",
      startTime: "",
      endTime: "",
    }));
  };

  const { mutateAsync, isLoading: isPaying } = useCreatePaymentIntentMutation();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.subjectId || !formData.date || !formData.startTime) {
      message.info("Please complete all required fields.");
      return;
    }
    mutateAsync(formData, {
      onSuccess: () => {
        setFormData(initialState);
        setVisible(false);
      },
    });
  };

  const steps = [
    {
      title: "Focus",
      content: (
        <>
          <Form.Item
            name="subject"
            label="Select Subject"
            rules={[{ required: true, message: "Please select a subject" }]}
          >
            <Select
              placeholder="Select a subject"
              onChange={(value) =>
                setFormData({ ...formData, subjectId: value })
              }
              size="large"
              className="w-full"
              value={formData?.subjectId}
            >
              {tutor?.data?.areaOfExpertise?.map(
                ({
                  subject,
                  id,
                  category,
                }: {
                  subject: string;
                  id: string;
                  category: string;
                }) => (
                  <Select.Option key={id} value={id} className="p-2">
                    {subject.charAt(0).toUpperCase() + subject.slice(1).toLowerCase()} - {category.charAt(0).toUpperCase() + category.slice(1).toLowerCase()}
                  </Select.Option>
                )
              ) || []}
            </Select>
          </Form.Item>

          <Form.Item
            name="type"
            label="Select Tutorship Type"
            rules={[
              { required: true, message: "Please select a tutorship type" },
            ]}
          >
            <Select
              placeholder="Online or In-person"
              onChange={(value) =>
                setFormData({ ...formData, tutorShipType: value })
              }
              size="large"
              className="w-full mb-4"
              value={formData?.tutorShipType}
            >
              {["Virtual", "One-on-one"].map((type) => {
                if (
                  tutor?.data?.availabilityType === type ||
                  tutor?.data?.availabilityType === "Both"
                ) {
                  return (
                    <Select.Option key={type} value={type}>
                      {type === "Virtual"
                        ? "Virtual Tutoring"
                        : "One-on-One Tutoring"}
                    </Select.Option>
                  );
                }
                return null;
              })}
            </Select>
            <div
              className={`flex items-center p-3 rounded-lg ${
                formData.tutorShipType === "Virtual"
                  ? "bg-blue-50 text-blue-700"
                  : formData.tutorShipType === "One-on-one"
                  ? "bg-green-50 text-green-700"
                  : "bg-gray-50 text-gray-500"
              }`}
            >
              <span className="mr-2">
                {formData.tutorShipType === "Virtual" ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm3 2h6v4H7V5zm8 8v2h1v-2h-1zm-2-2a2 2 0 012-2h1a2 2 0 012 2v2a2 2 0 01-2 2h-1a2 2 0 01-2-2v-2zm-6 0a2 2 0 012-2h1a2 2 0 012 2v2a2 2 0 01-2 2h-1a2 2 0 01-2-2v-2z"
                      clipRule="evenodd"
                    />
                  </svg>
                ) : formData.tutorShipType === "One-on-one" ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </span>
              <span className="text-sm">
                {formData.tutorShipType === "Virtual"
                  ? "Learn from anywhere with real-time online tutoring sessions"
                  : formData.tutorShipType === "One-on-one"
                  ? "Meet in person for face-to-face tutoring sessions"
                  : "Choose your preferred tutoring method"}
              </span>
            </div>
          </Form.Item>
        </>
      ),
    },
    {
      title: "Time",
      content: (
        <>
          <Form.Item
            name="timeSlot"
            label="Select a Time Slot"
            rules={[{ required: true, message: "Please select a time slot" }]}
          >
            {formData.startTime && formData.endTime ? (
              <div>
                <div className="text-lg mb-4">
                  <h3 className="font-semibold mb-2">Selected Time Slot:</h3>
                  {formData.startTime} - {formData.endTime} on{" "}
                  {new Date(formData.date)
                    .toLocaleDateString("en-US", {
                      weekday: "long",
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })
                    .replace(/(\d+)/, (match) => {
                      const num = parseInt(match);
                      const suffix = ["th", "st", "nd", "rd"][
                        (num > 3 && num < 21) || num % 10 > 3 ? 0 : num % 10
                      ];
                      return `${match}${suffix}`;
                    })}
                </div>
                <Button
                  type="default"
                  onClick={handleCancelTimeSlot}
                  className="bg-red-500 text-white py-1 px-3 rounded hover:bg-red-600"
                >
                  Cancel Selection
                </Button>
              </div>
            ) : (
              <div>
                {/* <h3 className="text-lg font-semibold mb-4">
                  Available Time Slots
                </h3> */}
                <div className="mt-6 overflow-y-scroll">
                  {tutor?.data?.weeklyAvailability?.length > 0 ? (
                    tutor.data.weeklyAvailability.map(
                      ({
                        day,
                        timeSlots,
                      }: {
                        day: string;
                        timeSlots: any[];
                      }) => (
                        <div className="flex mb-3" key={day}>
                          <div className="w-4/12 font-medium">{day}</div>
                          <div className="w-8/12">
                            {timeSlots?.length > 0 ? (
                              timeSlots.map(
                                (
                                  { startTime, endTime }: any,
                                  index: number
                                ) => (
                                  <div
                                    key={`${startTime}-${index}`}
                                    className="mb-3 flex justify-between items-center"
                                  >
                                    <span className="text-sm">
                                      {startTime} - {endTime}
                                    </span>
                                    <Button
                                      onClick={() =>
                                        handleSelectTimeSlot(
                                          day,
                                          startTime,
                                          endTime
                                        )
                                      }
                                      className="bg-green-500 text-white text-sm px-3 py-1 rounded hover:bg-green-600"
                                    >
                                      Select
                                    </Button>
                                  </div>
                                )
                              )
                            ) : (
                              <span className="text-sm text-gray-500">
                                No availability
                              </span>
                            )}
                          </div>
                        </div>
                      )
                    )
                  ) : (
                    <p>No available time slots.</p>
                  )}
                </div>
              </div>
            )}
          </Form.Item>
        </>
      ),
    },
    {
      title: "Note",
      content: (
        <>
          <Form.Item
            name="note"
            label="Note for the Tutor"
            rules={[{ required: true, message: "Please add a note" }]}
          >
            <TextArea
              rows={4}
              placeholder="E.g., I need help with calculus exam prep..."
              onChange={(e) =>
                setFormData({ ...formData, notes: e.target.value })
              }
              value={formData.notes}
            />
          </Form.Item>
        </>
      ),
    },
    {
      title: "Summary",
      content: (
        <div className="space-y-6">
          <Card
            size="small"
            title={
              <div className="flex items-center space-x-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 text-gray-600"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                  <path
                    fillRule="evenodd"
                    d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="font-medium">Booking Summary</span>
              </div>
            }
            className="bg-gray-50"
          >
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="w-24 flex-shrink-0">
                  <span className="text-gray-500 text-sm">Subject</span>
                </div>
                <div className="flex-1">
                  <span className="capitalize font-medium">
                    {tutor?.data?.areaOfExpertise?.find(
                      (item: any) => item.id === formData.subjectId
                    )?.subject || "-"}
                  </span>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-24 flex-shrink-0">
                  <span className="text-gray-500 text-sm">Type</span>
                </div>
                <div className="flex-1">
                  <span
                    className={`font-medium ${
                      formData.tutorShipType === "Virtual"
                        ? "text-blue-600"
                        : "text-green-600"
                    }`}
                  >
                    {formData.tutorShipType || "-"}
                  </span>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-24 flex-shrink-0">
                  <span className="text-gray-500 text-sm">Date</span>
                </div>
                <div className="flex-1">
                  <span className="font-medium">
                    {formData.date
                      ? new Date(formData.date).toLocaleDateString("en-US", {
                          weekday: "long",
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        })
                      : "-"}
                  </span>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-24 flex-shrink-0">
                  <span className="text-gray-500 text-sm">Time</span>
                </div>
                <div className="flex-1">
                  <span className="font-medium">
                    {formData.startTime && formData.endTime
                      ? `${formData.startTime} - ${formData.endTime}`
                      : "-"}
                  </span>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-24 flex-shrink-0">
                  <span className="text-gray-500 text-sm">Price</span>
                </div>
                <div className="flex-1">
                  <span className="font-medium text-primary900">
                    ${tutor?.data?.hourlyRate || 0}/hr
                  </span>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-24 flex-shrink-0">
                  <span className="text-gray-500 text-sm">Note</span>
                </div>
                <div className="flex-1">
                  <span className="font-medium italic">
                    "{formData.notes || "-"}"
                  </span>
                </div>
              </div>
            </div>
          </Card>

          <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-start text-gray-600">
              <span className="w-[40px] flex-shrink-0">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
              </span>
              <span className="text-sm flex-1">
                Please review your booking details carefully. After confirming,
                you'll be redirected to Stripe's secure payment platform to
                complete your transaction.
              </span>
            </div>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div style={{ maxWidth: 600, margin: "auto" }}>
      <Steps current={current} style={{ marginBottom: 32 }} size="small">
        {steps.map((item) => (
          <Step key={item.title} title={item.title} />
        ))}
      </Steps>

      {isLoading && !tutor && <Loader />}

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{ subject: "", type: "" }}
      >
        {steps[current].content}

        <div style={{ marginTop: 24 }} className="flex justify-between">
          {current > 0 && <Button onClick={prev}>Previous</Button>}
          {current < steps.length - 1 && (
            <Button type="primary" onClick={next} className="ml-auto">
              Next
            </Button>
          )}
          {current === steps.length - 1 && (
            <Button
              type="primary"
              htmlType="submit"
              className="ml-auto"
              onClick={handleSubmit}
              disabled={
                isPaying ||
                !formData.subjectId ||
                !formData.tutorShipType ||
                !formData.date ||
                !formData.startTime ||
                !formData.endTime ||
                !formData.notes
              }
            >
              {isPaying ? "Processing..." : "Confirm Booking"}
            </Button>
          )}
        </div>
      </Form>
    </div>
  );
};

export default TutorBookingStepper;
